import { getDb } from "@src/db/EpisodesDb";
import { Subscribable } from "./Subscribable";
import type { IAudio, IEpisode, TAudio, TEpisode } from "@src/types/AppTypes";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";

export class MusicItem extends Subscribable<() => void> {
  music: IAudio | null = null;
  constructor(item: IAudio | null) {
    super();
    this.music = item;
  }

  setEpisode = (item: IAudio | null) => {
    this.music = item;
    this.notify();
  };

  getMusic = () => {
    return this.music;
  };
}

export class EpisodeItem extends Subscribable<() => void> {
  episode: IEpisode | null = null;
  constructor(item: IEpisode | null) {
    super();
    this.episode = item;
  }

  setEpisode = (item: IEpisode | null) => {
    this.episode = item;
    this.notify();
  };

  getEpisode = () => {
    return this.episode;
  };
}

class IsDownloading extends Subscribable<() => void> {
  //
  isDownloading: boolean;

  constructor(isDownloading: boolean = false) {
    super();
    this.isDownloading = isDownloading;
  }

  setIsDownloading = (value: boolean) => {
    this.isDownloading = value;
    this.notify();
  };

  getIsDownloading = () => {
    return this.isDownloading;
  };
}

export class DownloadingMusicItem extends Subscribable<() => void> {
  isDownloading: IsDownloading = new IsDownloading();
  parts: Blob[] = [];
  id: number;
  start: number = 0;
  type: string;
  end: number = 0;
  item: MusicItem;
  controller: AbortController;
  chunkSize: number;
  progress: number = 0;

  constructor(
    id: number,
    type: string,
    end: number | null,
    item: IAudio | null,
    progress: number,
    isDownloading: boolean = true,
  ) {
    super();
    this.id = id;
    this.isDownloading.setIsDownloading(isDownloading);
    this.parts = [];
    this.type = type;
    this.progress = progress;
    this.item = new MusicItem(item);
    this.controller = new AbortController();
    if (item) {
      this.chunkSize = Math.floor(item.bytesize / 100);
      if (end) {
        this.start = end + 1;
        this.end = this.start + this.chunkSize;
      } else {
        this.end = this.chunkSize;
      }
      if (isDownloading) {
        setTimeout(() => {
          this.startDownload();
        });
      }
    } else {
      this.chunkSize = 1024; //1 mb
      this.end = this.chunkSize;
      this.start = 0;
    }
    window.addEventListener("beforeunload", this.handleAbort);
  }

  handleAbort = () => {
    this.controller.abort("manual");
    window.removeEventListener("beforeunload", this.handleAbort);
  };

  removeFromDownloading = async (id: number, removeDb: boolean = false) => {
    await downloadManager.downloadingMusic.removeItem(id, removeDb);
  };

  handleError = (message: string) => {
    Swal.fire({
      text: message,
      icon: "error",
      showConfirmButton: false,
      timer: 3000,
      showCloseButton: true,
      scrollbarPadding: false,
      backdrop: true, // for prevent background scroll when popup is open
    });
  };

  addToDownloadedMusic = async () => {
    try {
      const chunks = await getDb()
        ?.musicBlobData.where("fileId")
        .equals(this.item.getMusic()!.id)
        .sortBy("index");
      if (chunks?.length) {
        const blobs = chunks.map((item) => item.part);
        await getDb()
          ?.musics.update(this.item.getMusic()!.id, {
            blob: new Blob(blobs, {
              type: this.type === "audio/x-wav" ? "audio/wav" : this.type, // safari not support .x-wav content-type in blob.
            }),
          })
          .then(async () => {
            downloadManager.downloadingMusic.removeItem(
              this.item.getMusic()!.id,
            );
            for (const _ of chunks) {
              await getDb()
                ?.musicBlobData?.where("fileId")
                .equals(this.item.getMusic()!.id)
                .delete();
            }
          });
      }
    } catch (e) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available.",
        );
      } else {
        this.handleError("Download failed.");
      }

      await this.removeFromDownloading(this.item.getMusic()!.id, true);
    }
  };

  handleQuotaError = (e: any) => {
    if (e?.inner?.name === "QuotaExceededError") {
      this.handleError(
        "Download failed. There's not enough browser storage space available.",
      );
      return true;
    }
    return false;
  };
  downloadChunk = async () => {
    const handleAbortError = (e: any) => {
      const reason = e?.config?.signal?.reason;
      if (reason === "manual" || reason === "refresh" || reason === "load")
        return true;
      return false;
    };

    const music = this.item.getMusic();
    if (!music) return;

    try {
      const res = await axios.get(music.audio, {
        responseType: "blob",
        signal: this.controller.signal,
        withCredentials: true,
        headers: {
          range: `bytes=${this.start}-${this.end}`,
        },
      });

      if (res.status !== 206) return;

      await getDb()?.musicBlobData.add({
        part: res.data,
        index: this.progress + 1,
        fileId: music.id,
      });

      await getDb()?.downloadingMusics.update(music.id, {
        progress: this.progress + 1,
        end: this.end,
      });

      this.start = this.end + 1;
      this.end = Math.min(this.end + this.chunkSize, music.bytesize);

      this.progress += 1;
      this.notify();

      if (this.start <= music.bytesize) {
        await this.downloadChunk();
        return;
      }

      // Finalize download
      this.controller.abort("manual");
      this.notify();
      const d = await getDb()?.downloadingMusics.get(music.id);
      if (d) this.addToDownloadedMusic();
      await getDb()?.downloadingMusics.delete(music.id);
      this.notify();
    } catch (e: any) {
      if (e?.code === "ERR_CANCELED" && handleAbortError(e)) return;
      if (!this.handleQuotaError(e)) this.handleError("Download failed.");
      await this.removeFromDownloading(music.id, true);
    }
  };

  startDownload = async () => {
    if (this.item.getMusic()) {
      try {
        this.chunkSize = Math.floor(this.item.getMusic()!.bytesize / 100);
        await this.downloadChunk();
      } catch (e) {
        if (e?.inner?.name === "QuotaExceededError") {
          this.handleError(
            "Download failed. There's not enough browser storage space available.",
          );
        } else {
          this.handleError("Download failed.");
        }
        await this.removeFromDownloading(this.item.getMusic()!.id, true);
      }
    }
  };

  pauseDownloadInDb = async () => {
    try {
      await getDb()?.downloadingMusics.update(this.item.getMusic()!.id, {
        isDownloading: false,
      });
    } catch (e) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available.",
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getMusic()!.id, true);
    }
  };
  resumeDownloadInDb = async () => {
    try {
      await getDb()?.downloadingMusics.update(this.item.getMusic()!.id, {
        isDownloading: true,
      });
    } catch (e) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available.",
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getMusic()!.id, true);
    }
  };

  pauseDownload = () => {
    this.pauseDownloadInDb();
    this.isDownloading.setIsDownloading(false);
    this.controller.abort("manual");
    downloadManager.notify();
    this.notify();
  };

  resumeDownload = () => {
    this.resumeDownloadInDb();
    this.isDownloading.setIsDownloading(true);
    this.controller = new AbortController();
    this.startDownload();
    downloadManager.notify();
    this.notify();
  };
}

export class DownloadingVideoItem extends Subscribable<() => void> {
  isDownloading: IsDownloading = new IsDownloading();
  id: number;
  start: number = 0;
  type: string;
  end: number = 0;
  item: EpisodeItem;
  controller: AbortController;
  progress: number = 0;
  tsUrls: string[];
  parts: number;

  constructor(
    id: number,
    type: string,
    end: number | null,
    item: IEpisode | null,
    progress: number,
    isDownloading: boolean,
    tsUrls: string[],
    parts: number = 0,
  ) {
    super();
    this.id = id;
    this.isDownloading.setIsDownloading(isDownloading);
    this.type = type;
    this.progress = progress;
    this.item = new EpisodeItem(item);
    this.controller = new AbortController();
    this.tsUrls = tsUrls;
    this.parts = parts;
    if (item) {
      if (isDownloading) {
        setTimeout(() => {
          this.startDownload();
        });
      }
    }
    window.addEventListener("beforeunload", this.handleAbort);
  }

  handleAbort = () => {
    this.controller.abort("manual");
    window.removeEventListener("beforeunload", this.handleAbort);
  };

  removeFromDownloading = async (id: number, removeFromDb: boolean = false) => {
    await downloadManager.downloadingEpisodes.removeItem(id, removeFromDb);
  };

  handleError = (message: string) => {
    Swal.fire({
      text: message,
      icon: "error",
      showConfirmButton: false,
      timer: 3000,
      showCloseButton: true,
      scrollbarPadding: false,
      backdrop: true, // for prevent background scroll when popup is open
    });
  };

  addToDownloadedEpisodes = async () => {
    try {
      const chunks = await getDb()
        ?.episodesBlobData.where("fileId")
        .equals(this.item.getEpisode()!.id)
        .sortBy("index");
      if (chunks?.length) {
        await getDb()
          ?.episodes.update(this.item.getEpisode()!.id, {
            id: this.item.getEpisode()!.id,
            blob: [],
            content_type: this.item.getEpisode()!.content_type,
            is_purchased: this.item.getEpisode()!.is_purchased,
            number_of_episode: this.item.getEpisode()!.number_of_episode,
            number_of_season: this.item.getEpisode()!.number_of_season,
            title: this.item.getEpisode()!.title,
            downloading: false,
            downloaded: true,
          })
          .then(async () => {
            navigator.serviceWorker.ready.then(() => {
              if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                  message: "SyncDB",
                  id: Cookies.get("UIDT") ? Number(Cookies.get("UIDT")) : null,
                });
              }
            });
            downloadManager.downloadingEpisodes.removeItem(
              this.item.getEpisode()!.id,
            );
          });
      }
    } catch (e) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available.",
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getEpisode()!.id, true);
    }
  };

  pauseDownloadInDb = async () => {
    try {
      await getDb()?.downloadingVideos.update(this.item.getEpisode()!.id, {
        isDownloading: false,
      });
    } catch (e) {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available.",
        );
      } else {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(this.item.getEpisode()!.id, true);
    }
  };
  resumeDownloadInDb = async () => {
    try {
      await getDb()?.downloadingVideos.update(this.item.getEpisode()!.id, {
        isDownloading: true,
      });
    } catch {
      await this.removeFromDownloading(this.item.getEpisode()!.id, true);
    }
  };

  pauseDownload = () => {
    this.pauseDownloadInDb();
    this.isDownloading.setIsDownloading(false);
    this.controller.abort("manual");
    downloadManager.notify();
    this.notify();
  };

  resumeDownload = () => {
    this.resumeDownloadInDb();
    this.isDownloading.setIsDownloading(true);
    this.controller = new AbortController();
    this.startDownload();
    downloadManager.notify();
    this.notify();
  };

  downloadChunk = async () => {
    const episodeId = this.item.getEpisode()!.id;

    const handleAbortOrReturn = (e: any) => {
      const reason = e?.config?.signal?.reason;
      if (
        e?.code === "ERR_CANCELED" &&
        (reason === "manual" || reason === "refresh" || reason === "load")
      ) {
        return true;
      }
      return false;
    };

    const handleQuotaError = (e: any) => {
      if (e?.inner?.name === "QuotaExceededError") {
        this.handleError(
          "Download failed. There's not enough browser storage space available.",
        );
        return true;
      }
      return false;
    };

    try {
      const res = await axios.get(
        `${this.tsUrls[this.parts]}?cache_bust=${Date.now()}`,
        {
          responseType: "blob",
          signal: this.controller.signal,
          withCredentials: true,
        },
      );

      if (res.status !== 200) return;

      await getDb()?.episodesBlobData.add({
        part: res.data,
        index: this.parts,
        fileId: episodeId,
      });

      this.progress = Math.round((this.parts / this.tsUrls.length) * 100);
      await getDb()?.downloadingVideos.update(episodeId, {
        progress: this.progress,
        parts: this.parts,
        end: this.end,
      });

      this.parts++;
      this.notify();

      if (this.parts < this.tsUrls.length) {
        await this.downloadChunk();
        return;
      }

      this.controller.abort("manual");
      this.notify();

      const d = await getDb()?.downloadingVideos.get(episodeId);
      if (d) this.addToDownloadedEpisodes();
      await getDb()?.downloadingVideos.delete(episodeId);

      this.notify();
    } catch (e: any) {
      if (handleAbortOrReturn(e)) return;
      if (!handleQuotaError(e)) {
        this.handleError("Download failed.");
      }
      await this.removeFromDownloading(episodeId, true);
    }
  };

  startDownload = async () => {
    if (this.item.getEpisode()) {
      try {
        await this.downloadChunk();
      } catch {
        await this.removeFromDownloading(this.item.getEpisode()!.id, true);
      }
    }
  };
}

class DownloadingItems extends Subscribable<() => void> {
  //
  downloadingEpisodes: Map<number, DownloadingVideoItem> = new Map();

  getDownloadingEpisode = (id: number) => {
    return this.downloadingEpisodes.get(id);
  };

  getDownloadingEpisodes = () => {
    return this.downloadingEpisodes;
  };

  addItem = (item: DownloadingVideoItem) => {
    this.downloadingEpisodes.set(item.id, item);
    this.notify();
  };

  removeEpisodeFromDb = async (id: number) => {
    try {
      await getDb()?.downloadingVideos?.delete(id);
      await getDb()?.episodes?.delete(id);
      await getDb()?.episodesBlobData?.where("fileId").equals(id).delete();
      navigator.serviceWorker.ready.then(() => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            message: "SyncDB",
            id: Cookies.get("UIDT") ? Number(Cookies.get("UIDT")) : 0,
          });
        }
      });
    } catch (e) {
      console.error("Unable to remove downloaded episode");
    }
  };

  removeItem = async (id: number, removeFromDb: boolean = false) => {
    const item = this.downloadingEpisodes.get(id);
    if (item) {
      item.controller.abort("manual");
    }
    this.downloadingEpisodes.delete(id);
    this.notify();
    if (removeFromDb) {
      await this.removeEpisodeFromDb(id);
    }
  };
}

class DownloadingMusicItems extends Subscribable<() => void> {
  downloadingMusic: Map<number, DownloadingMusicItem> = new Map();

  getDownloadingSingleMusic = (id: number) => {
    return this.downloadingMusic.get(id);
  };

  getDownloadingMusic = () => {
    return this.downloadingMusic;
  };

  addItem = (item: DownloadingMusicItem) => {
    this.downloadingMusic.set(item.id, item);
    this.notify();
  };
  removeMusicFromDb = async (id: number) => {
    try {
      await getDb()?.downloadingMusics?.delete(id);
      await getDb()?.musics?.delete(id);
      await getDb()?.musicBlobData?.where("fileId").equals(id).delete();
    } catch (e) {
      console.error("Unable to remove downloaded music");
    }
  };

  removeItem = async (id: number, removeFromDb: boolean = false) => {
    const item = this.downloadingMusic.get(id);
    if (item) {
      item.controller.abort("manual");
    }
    this.downloadingMusic.delete(id);
    this.notify();
    if (removeFromDb) {
      await this.removeMusicFromDb(id);
    }
  };
}

export class IsDownloadManagerReady extends Subscribable<() => void> {
  isReady: boolean = false;

  setIsReady = (value: boolean) => {
    this.isReady = value;
    this.notify();
  };

  getIsReady = () => {
    return this.isReady;
  };
}

export class DownloadManager extends Subscribable<() => void> {
  isReady: IsDownloadManagerReady = new IsDownloadManagerReady();
  downloadingEpisodes: DownloadingItems = new DownloadingItems();
  downloadedEpisodes: Map<number, TEpisode> = new Map();
  downloadingMusic: DownloadingMusicItems = new DownloadingMusicItems();
  downloadedMusic: Map<number, TAudio> = new Map();

  getAndSetPendingDownloads = async (callback: () => void = () => {}) => {
    const db = getDb();
    if (!db) return;

    try {
      // Fetch all data in parallel
      const [
        downloadingEpisodesData,
        downloadingMusicData,
        downloadedEpisodesData,
        downloadedMusicData,
        episodesBlobData,
        musicBlobData,
      ] = await Promise.all([
        db.downloadingVideos.toArray(),
        db.downloadingMusics.toArray(),
        db.episodes.toArray(),
        db.musics.toArray(),
        db.episodesBlobData.toArray(),
        db.musicBlobData.toArray(),
      ]);

      // Helper to delete blobs beyond progress
      const deleteOutdatedBlobs = async (
        items: typeof downloadingEpisodesData | typeof downloadingMusicData,
        blobs: typeof episodesBlobData | typeof musicBlobData,
        blobTableName: "episodesBlobData" | "musicBlobData",
      ) => {
        for (const item of items) {
          const relevantBlobs = blobs?.filter(
            (blob) => blob.fileId === item.id,
          );
          if (relevantBlobs?.length > item.progress) {
            const blobsToDelete = relevantBlobs.filter(
              (blob) => blob.index > item.progress,
            );
            for (const blob of blobsToDelete) {
              await db[blobTableName]
                .where("fileId")
                .equals(blob.fileId)
                .and((t) => t.index > item.progress)
                .delete();
            }
          }
        }
      };

      // Helper to set downloaded items into Map
      const setDownloadedItems = (items: any[], map: Map<any, any>) => {
        for (const item of items) {
          map.set(item.id, item);
        }
      };

      // Process episodes
      if (downloadingEpisodesData?.length) {
        await deleteOutdatedBlobs(
          downloadingEpisodesData,
          episodesBlobData,
          "episodesBlobData",
        );
      }
      if (downloadedEpisodesData?.length) {
        setDownloadedItems(downloadedEpisodesData, this.downloadedEpisodes);
      }

      // Process music
      if (downloadingMusicData?.length) {
        await deleteOutdatedBlobs(
          downloadingMusicData,
          musicBlobData,
          "musicBlobData",
        );
        for (const item of downloadingMusicData) {
          this.downloadingMusic.addItem(
            new DownloadingMusicItem(
              item.id,
              item.type,
              item.end,
              null,
              item.progress,
              item.isDownloading,
            ),
          );
        }
      }
      if (downloadedMusicData?.length) {
        setDownloadedItems(downloadedMusicData, this.downloadedMusic);
      }

      callback();
    } catch (e) {
      console.error("Unable to sync db");
    }
  };

  addEpisodeDownload = (
    item: IEpisode,
    tsUrls: string[],
    end: number | null = null,
    progress: number = 0,
    isDownloading: boolean = true,
    parts: number = 0,
  ) => {
    this.downloadingEpisodes.addItem(
      new DownloadingVideoItem(
        item.id,
        item.content_type,
        end,
        item,
        progress,
        isDownloading,
        tsUrls,
        parts,
      ),
    );
  };

  addMusicDownload = (
    item: IAudio,
    end: number | null = null,
    progress: number = 0,
    isDownloading: boolean = true,
  ) => {
    this.downloadingMusic.addItem(
      new DownloadingMusicItem(
        item.id,
        item.content_type,
        end,
        item,
        progress,
        isDownloading,
      ),
    );
  };

  getDownloadingEpisodes = () => {
    return this.downloadingEpisodes;
  };

  getDownloadedEpisodes = () => {
    return this.downloadedEpisodes;
  };
}

export const downloadManager = new DownloadManager();
